import { memo } from 'react';
import type { InterviewRecord as InterviewRecordType } from '../../../../../fsm/jobs';
import { apiInstantToDate } from '../../../../../utils/apiInstant';
import {
  durationLabel,
  endedReasonLabel,
  eventLabel,
  integrityFacts,
} from '../../../utils/interviewRecord';
import styles from './index.module.css';

export interface InterviewRecordProps {
  /** The record avrio-rio keeps on the interview. Empty (no times, no events) for an interview written before it existed. */
  record?: InterviewRecordType | null;
  /** When the interview was completed; stands in for the end of an interview completed before the record existed. */
  completedAt?: number | string | null;
  /** Rio's summary of the interview. In the anonymised view the server has scrubbed it. */
  summary?: string | null;
  /** True when Rio ended the interview with nothing scored; the summary is then the main thing Rio has to say. */
  unscored?: boolean;
}

const when = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const clock = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
});

/** A time as a <time> element, so a screen reader and a copy-paste both get the instant; null for a time that is not one. */
const Time = ({
  value,
  format = when,
}: {
  value: number | string | null | undefined;
  format?: Intl.DateTimeFormat;
}) => {
  const date = apiInstantToDate(value);
  if (!date) return null;
  return <time dateTime={date.toISOString()}>{format.format(date)}</time>;
};

interface Fact {
  label: string;
  value: React.ReactNode;
}

/** The record's rows, only the ones there is something to say for. */
const factsOf = (
  record: InterviewRecordType | null | undefined,
  completedAt: InterviewRecordProps['completedAt'],
): Fact[] => {
  const end = record?.endedAt ?? completedAt ?? null;
  const facts: Fact[] = [];
  if (apiInstantToDate(record?.startedAt)) {
    facts.push({ label: 'Started', value: <Time value={record?.startedAt} /> });
  }
  if (apiInstantToDate(end)) {
    facts.push({ label: 'Ended', value: <Time value={end} /> });
  }
  const duration = durationLabel(record?.startedAt, end);
  if (duration) facts.push({ label: 'Duration', value: duration });
  const how = endedReasonLabel(record?.endedReason);
  if (how) facts.push({ label: 'How it ended', value: how });
  return facts;
};

/** Events with a time the page can read, in the order they happened. */
const eventsOf = (record: InterviewRecordType | null | undefined) =>
  (Array.isArray(record?.integrityEvents) ? record.integrityEvents : [])
    .filter((event) => !!event && typeof event === 'object')
    .map((event) => ({ event, date: apiInstantToDate(event.at) }))
    .sort(
      (a, b) =>
        (a.date?.getTime() ?? Infinity) - (b.date?.getTime() ?? Infinity),
    );

/**
 * The interview record on the candidate page (avrio-rio#254): when the interview started and ended, how long it took, how
 * it ended, and what the interview page recorded while it ran (the candidate leaving the page, returning, a paste that was
 * blocked), with the server's own time for each.
 *
 * Facts for context, not a finding: "left the page 2 times" is stated as that and nothing more, and none of it is part of
 * the fit score. A record the interview never kept (written before #254) says so rather than showing an empty box.
 */
export const InterviewRecord = memo(
  ({
    record,
    completedAt,
    summary,
    unscored = false,
  }: InterviewRecordProps) => {
    const facts = factsOf(record, completedAt);
    const events = eventsOf(record);
    const counted = integrityFacts(record);
    // "Nothing recorded" is only a claim about an interview that kept a record: an older one did not watch the page at all.
    const kept = !!apiInstantToDate(record?.startedAt);
    const text = typeof summary === 'string' ? summary.trim() : '';

    if (!text && facts.length === 0 && counted.length === 0 && !kept) {
      return (
        <p className={styles.empty}>
          No times were recorded for this interview.
        </p>
      );
    }

    return (
      <div className={styles.record}>
        {text && (
          <section className={styles.summary} aria-label="Rio's summary">
            <p className={styles.label}>
              {unscored ? 'What Rio could say' : "Rio's summary"}
            </p>
            <p className={styles.summaryText}>{text}</p>
          </section>
        )}
        {facts.length > 0 && (
          <dl className={styles.facts}>
            {facts.map((fact) => (
              <div className={styles.fact} key={fact.label}>
                <dt className={styles.factLabel}>{fact.label}</dt>
                <dd className={styles.factValue}>{fact.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {(counted.length > 0 || kept) && (
          <section className={styles.events} aria-label="Page events">
            <p className={styles.label}>Page events</p>
            <p className={styles.eventSummary}>
              {counted.length > 0
                ? `${counted.join(' · ')}.`
                : 'No page events were recorded.'}
            </p>
            {events.length > 0 && (
              <details className={styles.details}>
                <summary className={styles.summaryToggle}>
                  Each event, with its time
                </summary>
                <ol className={styles.eventList}>
                  {events.map(({ event, date }, index) => (
                    <li
                      key={`${index}-${event.event}`}
                      className={styles.event}
                    >
                      <span>{eventLabel(event.event)}</span>
                      {date && <Time value={event.at} format={clock} />}
                    </li>
                  ))}
                </ol>
              </details>
            )}
          </section>
        )}
      </div>
    );
  },
);

InterviewRecord.displayName = 'InterviewRecord';

export default InterviewRecord;
